import { useState, useMemo } from 'react';
import {
  Download,
  Users,
  Loader2,
  Printer,
  DollarSign,
  Receipt,
  TrendingUp,
  Globe,
  MapPin,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import { useLocations } from '@/hooks/useLocations';
import { useCurrency } from '@/hooks/useCurrency';
import { useReportPrint } from '@/hooks/useReportPrint';
import { format } from 'date-fns';
import { DateRangeSelect, type DateRangeValue } from '@/components/ui/date-range-select';
import { datePresets, presetValue } from '@/lib/datePresets';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@/components/ui/chart';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const DATE_RANGE_PRESETS = datePresets('today', 'week', 'month', 'last_month');

const CHART_COLORS = [
  'hsl(217, 91%, 60%)',  // blue
  'hsl(25, 95%, 53%)',   // orange
  'hsl(330, 81%, 60%)',  // pink
  'hsl(142, 76%, 36%)',  // green
  'hsl(263, 70%, 50%)',  // purple
  'hsl(47, 96%, 53%)',   // yellow
  'hsl(174, 72%, 40%)',  // teal
  'hsl(0, 84%, 60%)',    // red
];

export default function UserReports() {
  const { currentLocation } = useLocationStore();
  const { locations } = useLocations();
  const { formatCurrency } = useCurrency();
  const { printRef, handlePrint } = useReportPrint({ title: 'User report' });

  const [selectedLocationId, setSelectedLocationId] = useState<string>(currentLocation?.id || 'all');
  const [dateSel, setDateSel] = useState<DateRangeValue>(() => presetValue('week'));

  // Determine which location to use
  const queryLocationId = selectedLocationId === 'all' ? currentLocation?.id : selectedLocationId;

  // Calculate date filters
  const dateFilters = useMemo(
    () => ({ start: dateSel.start.toISOString(), end: dateSel.end.toISOString() }),
    [dateSel]
  );

  const dateRangeLabel = useMemo(() => {
    const start = new Date(dateFilters.start);
    const end = new Date(dateFilters.end);
    return `${format(start, 'MMM d, yyyy')} - ${format(end, 'MMM d, yyyy')}`;
  }, [dateFilters]);

  // Fetch orders with user data
  const { data: ordersData, isLoading: ordersLoading } = useQuery({
    queryKey: ['user-report-orders', queryLocationId, dateFilters],
    queryFn: async () => {
      if (!queryLocationId) return [];

      const { data, error } = await supabase
        .from('orders')
        .select(`
          id,
          total,
          tip_amount,
          customer_id,
          user_id,
          created_at,
          status,
          user:profiles(id, first_name, last_name, avatar_url)
        `)
        .eq('location_id', queryLocationId)
        .gte('created_at', dateFilters.start)
        .lte('created_at', dateFilters.end)
        .in('status', ['completed', 'ready', 'sent']);

      if (error) throw error;
      return data || [];
    },
    enabled: !!queryLocationId,
  });

  // Fetch payments for tip data
  const { data: paymentsData } = useQuery({
    queryKey: ['user-report-payments', queryLocationId, dateFilters],
    queryFn: async () => {
      if (!queryLocationId) return [];

      const { data, error } = await supabase
        .from('payments')
        .select(`
          id,
          amount,
          tip_amount,
          user_id,
          created_at,
          order:orders!inner(location_id, status)
        `)
        .eq('order.location_id', queryLocationId)
        .gte('created_at', dateFilters.start)
        .lte('created_at', dateFilters.end)
        .eq('status', 'completed');

      if (error) throw error;
      return data || [];
    },
    enabled: !!queryLocationId,
  });

  // Fetch time entries for clock data
  const { data: _timeEntries } = useQuery({
    queryKey: ['user-report-time', queryLocationId, dateFilters],
    queryFn: async () => {
      if (!queryLocationId) return [];

      // Get employees for this location
      const { data: employees } = await supabase
        .from('employees')
        .select('id, user_id')
        .eq('location_id', queryLocationId);

      if (!employees?.length) return [];

      const { data, error } = await supabase
        .from('time_entries')
        .select(`
          id,
          employee_id,
          clock_in_at,
          clock_out_at,
          break_minutes,
          employee:employees(user_id)
        `)
        .in('employee_id', employees.map(e => e.id))
        .gte('clock_in_at', dateFilters.start)
        .lte('clock_in_at', dateFilters.end);

      if (error) throw error;
      return data || [];
    },
    enabled: !!currentLocation?.id,
  });

  const isLoading = ordersLoading;

  // Process user data
  const userData = useMemo(() => {
    if (!ordersData?.length) return [];

    // Group by user
    const userMap = new Map<string, {
      id: string;
      name: string;
      avatarUrl: string | null;
      orders: typeof ordersData;
      firstAction: Date | null;
      lastAction: Date | null;
      customers: Set<string>;
      revenue: number;
      tips: number;
    }>();

    ordersData.forEach(order => {
      const userId = order.user_id || 'unknown';
      const user = order.user as any;
      const name = user 
        ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown'
        : 'Unknown';

      if (!userMap.has(userId)) {
        userMap.set(userId, {
          id: userId,
          name,
          avatarUrl: user?.avatar_url || null,
          orders: [],
          firstAction: null,
          lastAction: null,
          customers: new Set(),
          revenue: 0,
          tips: 0,
        });
      }

      const userData = userMap.get(userId)!;
      userData.orders.push(order);
      userData.revenue += order.total || 0;
      userData.tips += order.tip_amount || 0;

      if (order.customer_id) {
        userData.customers.add(order.customer_id);
      }

      const orderDate = new Date(order.created_at);
      if (!userData.firstAction || orderDate < userData.firstAction) {
        userData.firstAction = orderDate;
      }
      if (!userData.lastAction || orderDate > userData.lastAction) {
        userData.lastAction = orderDate;
      }
    });

    // Add tip data from payments
    if (paymentsData) {
      paymentsData.forEach(payment => {
        const userId = payment.user_id || 'unknown';
        if (userMap.has(userId) && payment.tip_amount) {
          // Tips are already counted from orders, but we could use this for additional tracking
        }
      });
    }

    return Array.from(userMap.values())
      .map(user => ({
        ...user,
        receipts: user.orders.length,
        customerCount: user.customers.size,
      }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [ordersData, paymentsData]);

  // Summary stats
  const summaryStats = useMemo(() => {
    if (!userData.length) return null;

    const totalRevenue = userData.reduce((sum, u) => sum + u.revenue, 0);
    const totalTips = userData.reduce((sum, u) => sum + u.tips, 0);
    const totalReceipts = userData.reduce((sum, u) => sum + u.receipts, 0);
    const activeUsers = userData.filter(u => u.receipts > 0).length;

    return {
      activeUsers,
      totalRevenue,
      totalTips,
      totalReceipts,
      avgRevenuePerUser: activeUsers > 0 ? totalRevenue / activeUsers : 0,
    };
  }, [userData]);

  // Revenue chart data
  const revenueChartData = useMemo(() => {
    return userData.slice(0, 10).map((user, idx) => ({
      name: user.name.length > 12 ? user.name.slice(0, 12) + '...' : user.name,
      fullName: user.name,
      revenue: user.revenue,
      fill: CHART_COLORS[idx % CHART_COLORS.length],
    }));
  }, [userData]);

  // Receipts pie chart data
  const receiptsChartData = useMemo(() => {
    const total = userData.reduce((sum, u) => sum + u.receipts, 0);
    return userData.slice(0, 8).map((user, idx) => ({
      name: user.name.length > 10 ? user.name.slice(0, 10) + '...' : user.name,
      fullName: user.name,
      value: user.receipts,
      percentage: total > 0 ? (user.receipts / total) * 100 : 0,
      fill: CHART_COLORS[idx % CHART_COLORS.length],
    }));
  }, [userData]);

  const chartConfig: ChartConfig = {
    revenue: { label: 'Revenue', color: CHART_COLORS[0] },
    value: { label: 'Receipts', color: CHART_COLORS[1] },
  };

  // Export CSV
  const exportToCSV = () => {
    if (!userData.length) return;

    const headers = ['User', 'From', 'To', 'First Action', 'Last Action', '# Customers', '# Receipts', 'Revenue', 'Tips'];
    const rows = userData.map(user => [
      user.name,
      format(new Date(dateFilters.start), 'yyyy-MM-dd'),
      format(new Date(dateFilters.end), 'yyyy-MM-dd'),
      user.firstAction ? format(user.firstAction, 'yyyy-MM-dd HH:mm') : '-',
      user.lastAction ? format(user.lastAction, 'yyyy-MM-dd HH:mm') : '-',
      user.customerCount,
      user.receipts,
      user.revenue.toFixed(2),
      user.tips.toFixed(2),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">User Reports</h1>
          <p className="text-sm text-muted-foreground">
            Track employee performance, revenue, and activity
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={handlePrint}>
            <Printer className="h-4 w-4" />
            Print
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={exportToCSV}>
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Location Selector */}
        <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select location" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              <div className="flex items-center gap-2">
                <Globe className="h-3.5 w-3.5" />
                <span>All locations</span>
              </div>
            </SelectItem>
            {locations.length > 0 && <div className="my-1 h-px bg-border" />}
            {locations.map((loc) => (
              <SelectItem key={loc.id} value={loc.id}>
                <div className="flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>{loc.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <DateRangeSelect presets={DATE_RANGE_PRESETS} value={dateSel} onChange={setDateSel} />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Active Users</span>
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : summaryStats?.activeUsers || 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Revenue</span>
              <DollarSign className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : formatCurrency(summaryStats?.totalRevenue || 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Receipts</span>
              <Receipt className="h-4 w-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : summaryStats?.totalReceipts || 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Tips</span>
              <TrendingUp className="h-4 w-4 text-amber-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : formatCurrency(summaryStats?.totalTips || 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Users Revenue Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Users Revenue</CardTitle>
                <CardDescription>Revenue by team member</CardDescription>
              </CardHeader>
              <CardContent>
                {revenueChartData.length > 0 ? (
                  <ChartContainer config={chartConfig} className="h-[300px]">
                    <BarChart data={revenueChartData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="name"
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => `$${value}`}
                      />
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          labelFormatter={(_, payload: any) => {
                            if (payload?.[0]?.payload?.fullName) {
                              return payload[0].payload.fullName;
                            }
                            return '';
                          }}
                          valueFormatter={(value) => formatCurrency(value as number)}
                        />
                      }
                    />
                    <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                      {revenueChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ChartContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  No revenue data
                </div>
              )}
            </CardContent>
          </Card>

          {/* User Receipts Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle># User Receipts</CardTitle>
              <CardDescription>Distribution of receipts by user</CardDescription>
            </CardHeader>
            <CardContent>
              {receiptsChartData.length > 0 ? (
                <ChartContainer config={chartConfig} className="h-[300px]">
                  <PieChart>
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          labelFormatter={(_, payload: any) => {
                            if (payload?.[0]?.payload?.fullName) {
                              return payload[0].payload.fullName;
                            }
                            return '';
                          }}
                          valueFormatter={(value) => `${value} receipts`}
                        />
                      }
                    />
                      <Pie
                        data={receiptsChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percentage }) => `${name} ${percentage?.toFixed(0)}%`}
                        labelLine={false}
                      >
                        {receiptsChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <ChartLegend
                        content={<ChartLegendContent nameKey="name" />}
                        verticalAlign="bottom"
                      />
                    </PieChart>
                  </ChartContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No receipts data
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* User Reporting Table */}
          <Card>
            <CardHeader>
              <CardTitle>User Reporting</CardTitle>
              <CardDescription>
                Detailed breakdown by team member
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead className="w-[50px]"></TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>First Action</TableHead>
                    <TableHead>Last Action</TableHead>
                    <TableHead className="text-right"># Customers</TableHead>
                    <TableHead className="text-right"># Receipts</TableHead>
                    <TableHead className="text-right">Revenue Created</TableHead>
                    <TableHead className="text-right">Revenue Received</TableHead>
                    <TableHead className="text-right">Tips</TableHead>
                    <TableHead className="w-[60px]">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {userData.map((user, idx) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user.avatarUrl || undefined} />
                          <AvatarFallback className="text-xs" style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] + '33' }}>
                            {user.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      </TableCell>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {format(new Date(dateFilters.start), 'yyyy-MM-dd')}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {format(new Date(dateFilters.end), 'yyyy-MM-dd')}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {user.firstAction ? format(user.firstAction, 'yyyy-MM-dd') : '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {user.lastAction ? format(user.lastAction, 'yyyy-MM-dd') : '-'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {user.customerCount}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {user.receipts}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(user.revenue)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(user.revenue)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(user.tips)}
                      </TableCell>
                      <TableCell>
                        <Button variant="link" size="sm" className="text-primary p-0 h-auto">
                          More info
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {userData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                        No user data found for this period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {/* Print template */}
      <div ref={printRef} className="print-source">
        <div className="print-header">
          <div>
            <h1>User Report</h1>
            <div className="print-meta">
              {currentLocation?.name || 'All Locations'} • {dateRangeLabel}
            </div>
          </div>
          <div className="print-meta">
            Generated on {new Date().toLocaleString()}
          </div>
        </div>

        <div className="print-summary">
          <div className="print-card">
            <div className="label">Active Users</div>
            <div className="value">{summaryStats?.activeUsers || 0}</div>
          </div>
          <div className="print-card">
            <div className="label">Total Revenue</div>
            <div className="value">{formatCurrency(summaryStats?.totalRevenue || 0)}</div>
          </div>
          <div className="print-card">
            <div className="label">Total Receipts</div>
            <div className="value">{summaryStats?.totalReceipts || 0}</div>
          </div>
          <div className="print-card">
            <div className="label">Total Tips</div>
            <div className="value">{formatCurrency(summaryStats?.totalTips || 0)}</div>
          </div>
        </div>

        <div className="print-section">
          <h2>User Performance</h2>
          {userData.length > 0 ? (
            <table className="print-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>First Action</th>
                  <th>Last Action</th>
                  <th># Customers</th>
                  <th># Receipts</th>
                  <th>Revenue</th>
                  <th>Tips</th>
                </tr>
              </thead>
              <tbody>
                {userData.map((user) => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.firstAction ? format(user.firstAction, 'MMM d, yyyy') : '-'}</td>
                    <td>{user.lastAction ? format(user.lastAction, 'MMM d, yyyy') : '-'}</td>
                    <td>{user.customerCount}</td>
                    <td>{user.receipts}</td>
                    <td>{formatCurrency(user.revenue)}</td>
                    <td>{formatCurrency(user.tips)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="print-meta">No user data found.</div>
          )}
        </div>
      </div>
    </div>
  );
}
